import { CompactNumber } from "@/shared/ui/CompactNumber";
import { Card, CardContent } from "@/components/ui/card";
import {
  DollarSignIcon,
  MapPinIcon,
  PercentIcon,
  ShoppingCartIcon,
  TrendingUpIcon,
} from "lucide-react";

type SalesReportsSummaryCardsProps = {
  totalRevenue: number;
  totalOrders: number;
  avgOrderValue: number;
  grossMargin: number;
  topBranchName: string | null;
  isLoading?: boolean;
};

export function SalesReportsSummaryCards({
  totalRevenue,
  totalOrders,
  avgOrderValue,
  grossMargin,
  topBranchName,
  isLoading,
}: SalesReportsSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent className="p-6">
              <div className="h-10 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-6 w-16 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <Card className="rounded-xl border border-blue-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <DollarSignIcon className="size-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ລາຍໄດ້ລວມ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalRevenue} suffix=" LAK" />
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-green-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
            <ShoppingCartIcon className="size-5 text-green-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຈຳນວນອໍເດີ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalOrders} />
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-purple-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
            <TrendingUpIcon className="size-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຄ່າສະເລ່ຍຕໍ່ອໍເດີ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={avgOrderValue} suffix=" LAK" />
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-emerald-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
            <PercentIcon className="size-5 text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ເງິນກຳໄລສຸດຍອດ</p>
            <p className="font-semibold text-xl tabular-nums">
              {grossMargin.toFixed(2)}%
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-orange-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
            <MapPinIcon className="size-5 text-orange-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ສາຂາອັນດັບ 1</p>
            <p className="truncate font-semibold text-lg tabular-nums">
              {topBranchName ?? "–"}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
